import { Text, ThreeDotLoader } from '@netapp/bxp-design-system-react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { memo } from 'react';
import stepIndicatorStyles from './StepIndicator.module.scss';

/**
 * StepIndicator - Shows thinking/processing state aligned to the left
 * Displayed during AI processing, hidden when TEXT_MESSAGE_END is received
 * 
 * @param {Object} props
 * @param {string} props.id - Unique identifier for the component instance
 * @param {Object} props.data - Descriptor data containing step info
 * @param {string} props.stepMessage - Step message (direct prop)
 * @param {boolean} props.isStreaming - Whether content is actively streaming
 * @param {string} props.className - Additional CSS class
 * @param {function} props.onAction - Callback for actions
 */

const StepIndicator = memo(({ id, data, stepMessage, isStreaming, className, onAction }) => {
  // Support both 'data' (from descriptor) and 'stepMessage' (direct prop)
  const message = data?.step || data || stepMessage || 'Thinking';
  const displayMessage = typeof message === 'string' ? message : 'Thinking';

  return (
    <div
      className={classNames(stepIndicatorStyles["indicator-row"], className)}
      data-component-id={id}
    >
      <div className={stepIndicatorStyles["step-indicator-container"]}>
        <Text>{displayMessage}</Text>
        <ThreeDotLoader />
      </div>
    </div>
  );
});

StepIndicator.displayName = 'StepIndicator';

StepIndicator.propTypes = {
  id: PropTypes.string,
  /** Descriptor data containing step info (can be string or object with 'step' property) */
  data: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      step: PropTypes.string
    })
  ]),
  stepMessage: PropTypes.string,
  isStreaming: PropTypes.bool,
  className: PropTypes.string,
  onAction: PropTypes.func
};

StepIndicator.defaultProps = {
  id: undefined,
  data: undefined,
  stepMessage: 'Thinking',
  isStreaming: false,
  className: '',
  onAction: undefined
};

// Catalog configuration for registry
StepIndicator.catalogConfig = {
  type: 'StepIndicator',
  category: 'loading',
  defaultProps: {
    stepMessage: 'Thinking'
  }
};

export default StepIndicator;